import { AsyncPipe } from "@angular/common";
import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { BusySpinnerComponent } from "@components/busy/busy-spinner/busy-spinner.component";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { BlockComponent } from "@components/container-block/block.component";
import { HeaderComponent } from "@components/header/header.component";
import { MessagePanelComponent } from "@components/message-panel/message-panel.component";
import { Viol3statusIndicatorComponent } from "@components/viol3status-indicator/viol3status-indicator.component";
import { VirkesorderstatusIndicatorComponent } from "@components/virkesorderstatus-indicator/virkesorderstatus-indicator.component";
import { NgbActiveModal, NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { TranslateDirective, TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Subject, forkJoin } from "rxjs";
import { filter } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { RaskRoller, VirkesorderStatus } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import {
    InitiateArbetsorderViol3SynchronizationCommand
} from "~app/features/administration/systemadministration/tabs/sysadm-viol3/commands/InitiateArbetsorderViol3SynchronizationCommand";
import { ArbestorderDestineringsalternativViewModel } from "~app/features/planera/arbetsorder-destineringsalternativ/arbetsorder-destineringsalternativ.models";
import {
    ArbetsorderDestineringsalternativManuellaComponent
} from "~app/features/planera/arbetsorder-destineringsalternativ/manuella/arbetsorder-destineringsalternativ-manuella.component";
import { MergeAllDestineringsalternativCommand } from "~app/features/planera/commands/MergeAllDestineringsalternativCommand";
import { ArbetsorderService } from "~app/features/planera/services/arbetsorder.service";
import { ArbetsorderEventStore } from "~app/features/shared/services/arbetsorderEventStore.service";
import { ITplArbetsorderDetailHandelssortiment, ITplArbetsorderDetails, IVirkesorderstatus } from "../../models/dtos";

import { TplService } from "../../services/tpl.service";
import { DestineringsraderUpdatedEvent } from "../tpl-omdestinera/components/begransa-virkesorderrader-modal/begransa-virkesorderrader-events";
import { BegransaVirkesorderraderModalComponent } from "../tpl-omdestinera/components/begransa-virkesorderrader-modal/begransa-virkesorderrader-modal.component";
import { RuttChangedEvent, TplTransportAvstandEventHandler } from "../tpl-transportavstand-modal/tpl-transportavstand-eventhandler";
import { IArbetsorderArguments, ICallbackResult } from "./tpl-arbetsorder-info-modal-models";
import {
    ITplArbetsorderListDetailsHeader,
    ITplArbetsorderListHeader,
    ITplArbetsorderListRow,
    TplArbetsorderListColumnType,
    TplArbetsorderListModel
} from "./tpl-arbetsorder-list-model";
import { TplArbetsorderListComponent } from "./tpl-arbetsorder-list.component";

@Component({
    selector: "rask-tpl-arbetsorder-info-modal",
    templateUrl: "./tpl-arbetsorder-info-modal.component.html",
    styleUrls: ["./tpl-arbetsorder-info-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        CheckboxComponent,
        FormsModule,
        BlockComponent,
        MessagePanelComponent,
        TranslateDirective,
        VirkesorderstatusIndicatorComponent,
        Viol3statusIndicatorComponent,
        TplArbetsorderListComponent,
        ButtonComponent,
        BusySpinnerComponent,
        RouterLink,
        AsyncPipe,
        TranslatePipe
    ]
})
export class TplArbetsorderInfoModalComponent implements OnInit, OnDestroy {
    arbetsorder: ITplArbetsorderDetails;
    arbetsorderId: number;
    busyUpdating: boolean;
    hasAddedDestineringsalternativ = false;
    isSelected = false;
    listContent: TplArbetsorderListModel;
    messageItems: IMessageModel[] = [];
    messageItemsAsObservable: BehaviorSubject<IMessageModel[]>;
    messageLabels = [
        { severity: MessageSeverity.Info,
            labelText: "Hantera arbetsorderns rader" }
    ];
    senastSkickadDatum: string;
    showSelected = false;
    virkesorderStatus = VirkesorderStatus.Virkesorderstatusar;

    private readonly activeModal = inject(NgbActiveModal);
    private readonly arbetsorderEventStore = inject(ArbetsorderEventStore);
    private readonly arbetsorderService = inject(ArbetsorderService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly eventHandler: TplTransportAvstandEventHandler;
    private readonly eventService = inject(EventService);
    private readonly modalService = inject(NgbModal);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly raskModalService = inject(RaskModalService);
    private readonly tplService = inject(TplService);
    private readonly translate = inject(TranslateService);

    constructor() {
        this.eventHandler = new TplTransportAvstandEventHandler(this.eventService);
        this.messageItemsAsObservable = new BehaviorSubject<IMessageModel[]>([]);
    }

    static open(
        arbetsorderArgs: IArbetsorderArguments,
        modalService: NgbModal,
        callBack: (result: ICallbackResult) => void = null) {
        const options = {
            keyboard: false,
            backdrop: "static",
            centered: true,
            windowClass: "ao-info-modal-size"
        } as NgbModalOptions;
        const modalRef = modalService.open(this, options);
        modalRef.componentInstance.arbetsorderId = arbetsorderArgs.arbetsorderId;

        if (arbetsorderArgs.showSelected) {
            modalRef.componentInstance.showSelected = true;
            modalRef.componentInstance.isSelected = arbetsorderArgs.isSelected;
        }

        if (callBack) {
            modalRef.result.then(r => callBack(
                {
                    isSelected: modalRef.componentInstance.isSelected,
                    ngbModalResult: r,
                    hasAddedDestineringsalternativ: modalRef.componentInstance.hasAddedDestineringsalternativ
                }));
        } else {
            modalRef.result.then(r => modalRef.close(r));
        }
    }

    addManuellaDestineringsalternativ() {
        const ahs$ = this.arbetsorderService.getArbetsorderHandelssortiment(this.arbetsorder.id);
        const destineringar$ = this.arbetsorderService.getDestineringsalternativ(this.arbetsorder.id);

        forkJoin([
            ahs$,
            destineringar$
        ])
            .subscribe(([
                ah,
                dest
            ]) => {
                const arbetsorderHandelssortimentsLista = ah.arbetsorderHandelssortiment;
                const destineringsalternativLista = dest.destineringsalternativ;
                const viewModel = new ArbestorderDestineringsalternativViewModel(arbetsorderHandelssortimentsLista, destineringsalternativLista);
                const valdaManuella = [];

                viewModel.arbetsorderHandelssortiment.forEach(hs => {
                    hs.destineringsalternativ
                        .filter(x => x.isManuellRad)
                        .forEach(da => {
                            valdaManuella.push({
                                foradlatHandelssortimentId: da.foradlatHandelssortimentId,
                                handelssortimentId: da.arbetsorderHandelssortiment.handelssortimentId,
                                mottagningsplatsId: da.mottagningsplatsId
                            });
                        });
                });

                const data = {
                    arbetsorder: this.arbetsorder,
                    valdaManuellaRader: valdaManuella,
                    checkAddArbetsorderIntressent: true
                };

                this.raskModalService.openAsync(
                    "Manuella destineringsalternativ",
                    ArbetsorderDestineringsalternativManuellaComponent,
                    "lg",
                    true,
                    data)
                    .subscribe((response) => {
                        if (response === true) {
                            this.hasAddedDestineringsalternativ = true;
                        }
                        this.initView();
                    });
            });
    }

    canDestinera(): boolean {
        return !this.tplService.isExternArbetsorder(this.arbetsorder);
    }

    canEdit = () => this.authenticationService.canEdit();

    close() {
        this.activeModal.close();
    }

    getArbetsorder($event: any, arbetsorderId: number) {
        $event.preventDefault();
        const properties = {
            arbetsorderId: arbetsorderId
        };

        this.tplService.getTplArbetsorderDetails(properties).subscribe((res: ITplArbetsorderDetails) => {
            this.arbetsorder = res;
        });
    }

    handleMessagePanelClick(event: IMessageModel) {
        if (event.anchor === "#begransa-rader") {
            BegransaVirkesorderraderModalComponent.open(this.arbetsorder.id, null, this.modalService);
        }
    }

    initView() {
        const properties = {
            arbetsorderId: this.arbetsorderId
        };

        const query = this.tplService.getTplArbetsorderDetails(properties);

        this.busyService.addWork(query).subscribe((res: ITplArbetsorderDetails) => {
            this.arbetsorder = res;
            this.createListModel();
            this.setRowMessage();
        });
    }

    ngOnDestroy(): void {
        this.eventHandler.unsubscribe();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.initView();

        this.eventHandler.onRuttChanged(e => {
            this.onRuttChanged(e);
        });
        this.eventHandler.onDestineringsraderUpdated(e => {
            this.onDestineringsAlternativChanged(e);
        });
    }

    onSelectedChanged($event: boolean) {
        this.isSelected = $event;
    }

    tryClose($event: any) {
        if (!$event.ctrlKey) {
            this.close();
        }
    }

    uppdateraDestineringsalternativ() {
        const command = new MergeAllDestineringsalternativCommand(x => {
            x.arbetsorderId = this.arbetsorderId;
            x.checkAddIntressent = true;
        });
        // Detta command körs alltid isolerat - oavsett vad som ligger i stacken hos commandService
        const local = this.commandService.createInstance();
        local.addTransactionCommand(command);

        const work = local.pushCommandQueueTransaction();

        this.busyUpdating = true;
        this.arbetsorderEventStore.addArbetsorderList([this.arbetsorderId]);
        this.arbetsorderEventStore.destineringsalternativMergeCompleted$
            .pipe(filter(x => x.arbetsorderId === this.arbetsorder.id))
            .subscribe(x => {
                this.refreshDataAfterCompleted();
            });

        this.busyService.addWork(work).subscribe(x => {
            this.initView();
        });
    }

    uppdateraVirkesordrar() {
        const local = this.commandService.createInstance();
        if (this.arbetsorder.viol3) {
            const command = new InitiateArbetsorderViol3SynchronizationCommand(x => {
                x.arbetsorderId = this.arbetsorderId;
                x.checkAddIntressent = true;
            });
            local.addTransactionCommand(command);
        }

        // Detta command körs alltid isolerat - oavsett vad som ligger i stacken hos commandService
        const work = local.pushCommandQueueTransaction();

        this.busyUpdating = true;
        this.arbetsorderEventStore.addArbetsorderList([this.arbetsorderId]);
        this.arbetsorderEventStore.arbetsorderSynchFinished$
            .pipe(filter(x => x.arbetsorderId === this.arbetsorder.id))
            .subscribe(x => {
                this.refreshDataAfterCompleted();
            });

        this.busyService.addWork(work).subscribe(x => {
            this.initView();
        });
    }

    voStatus = (status?: IVirkesorderstatus) =>
        status ? `${this.virkesorderStatus.find(x => x.key === status.status).value} (${status.nummer})`
            : `${this.virkesorderStatus.find(x => x.key === "").value}`;

    private createListModel() {
        const headers: ITplArbetsorderListHeader[] = [
            { title: this.translate.instant("TPL_AODETAILS_HANDELSSORTIMENT"),
                width: "200px" },
            { title: this.translate.instant("TPL_AODETAILS_MOTTAGNINGSPLATS"),
                width: "300px" },
            { title: this.translate.instant("TPL_AODETAILS_TRANSPORTAVSTAND"),
                width: "140px" },
            { title: this.translate.instant("TPL_AODETAILS_PLANERAD"),
                width: "140px" },
            { title: this.translate.instant("TPL_AODETAILS_VAGLAGER"),
                width: "140px" },
            { title: this.translate.instant("TPL_AODETAILS_SKOGSLAGER"),
                width: "140px" },
            { title: this.translate.instant("TPL_AODETAILS_TRANSPORTFORETAG"),
                width: "340px" },
            { title: this.translate.instant("TPL_AODETAILS_TRANSPORTTIDSPAN"),
                width: "240px" }
        ];

        const rows = [] as ITplArbetsorderListRow[];

        this.arbetsorder.handelssortiment.forEach((hs: ITplArbetsorderDetailHandelssortiment) => {
            const row = {
                handelssortimentId: hs.id,
                isDetailsOpen: false,
                columns: [
                    {
                        content: hs.namn,
                        width: "200px",
                        typ: TplArbetsorderListColumnType.text
                    },
                    {
                        content: hs.mottagningsplatser,
                        width: "300px",
                        typ: TplArbetsorderListColumnType.nameList
                    },
                    {
                        content: hs.transportavstand,
                        width: "140px",
                        typ: TplArbetsorderListColumnType.distance
                    },
                    {
                        content: hs.planeradVolym,
                        width: "140px",
                        typ: TplArbetsorderListColumnType.text
                    },
                    {
                        content: hs.vaglager,
                        width: "140px",
                        typ: TplArbetsorderListColumnType.text
                    },
                    {
                        content: hs.skogslager,
                        width: "140px",
                        typ: TplArbetsorderListColumnType.text
                    },
                    {
                        content: hs.transportforetag,
                        width: "340px",
                        typ: TplArbetsorderListColumnType.text
                    },
                    {
                        content: hs.transporttidspan,
                        width: "200px",
                        typ: TplArbetsorderListColumnType.text
                    }
                ]
            } as ITplArbetsorderListRow;

            this.setActiveAvstand(hs);

            const detailsRow = {
                columns: [
                    {
                        content: hs.valbaraMottagnignsplatser,
                        width: "300px",
                        typ: TplArbetsorderListColumnType.nameList
                    },
                    {
                        content: hs.valbaraTransportAvstand,
                        width: "140px",
                        typ: TplArbetsorderListColumnType.distance
                    }
                ]
            } as ITplArbetsorderListRow;

            row.detailsRow = detailsRow;
            rows.push(row);
        });

        const detailsHeader: ITplArbetsorderListDetailsHeader[] = [
            {
                title: "",
                width: "10px",
                hasBorder: false
            },
            {
                title: "",
                width: "190px",
                hasBorder: false
            },
            {
                title: this.translate.instant("TPL_AODETAILS_VALBARA_MOTAGNINGSPLATSER"),
                width: "440px",
                hasBorder: true
            }
        ];

        this.listContent = new TplArbetsorderListModel(headers, rows, detailsHeader);
    }

    private isSent(): boolean {
        return this.arbetsorder.hasBeenSent;
    }

    private onDestineringsAlternativChanged(e: DestineringsraderUpdatedEvent) {
        if (e.arbetsorderId !== this.arbetsorder.id) {
            return;
        }

        this.arbetsorder.antalRader = e.antalRader;
        this.initView();
    }

    private onRuttChanged(e: RuttChangedEvent) {
        if (e.arbetsorderId !== this.arbetsorder.id) {
            return;
        }

        this.initView();
    }

    private refreshDataAfterCompleted() {
        this.arbetsorderEventStore.removeArbetsorderList([this.arbetsorderId]);
        this.busyUpdating = false;
        this.initView();
    }

    private setActiveAvstand(hs: ITplArbetsorderDetailHandelssortiment) {
        hs.valbaraMottagnignsplatser.forEach((x, index) => {
            hs.valbaraTransportAvstand[index].isActive = x.aktiv;
        });
    }

    private setRowMessage() {
        let message: string;

        const hanteraRader = "Hantera rader";
        const justeradeRader = "Antal rader på arbetsordern har justerats.";

        const index = this.messageItemsAsObservable.value.map(e => e.anchorText).indexOf(hanteraRader);

        if (index > -1) {
            this.messageItemsAsObservable.value.splice(index);
        }

        if (this.arbetsorder.antalRader === 0 && !this.arbetsorder.isDestineringsalternativJusterad) {
            return;
        }

        if ((this.isSent() && this.arbetsorder.antalRader > 60) || this.arbetsorder.antalRader > 50) {
            const text = this.isSent() ? "uppdaterar" : "skickar";
            message = `Arbetsordern har ${this.arbetsorder.antalRader} rader. Du behöver begränsa antalet innan du ${text} virkesorder.`;
        } else if (this.arbetsorder.isDestineringsalternativJusterad || this.isSent() && this.arbetsorder.antalRader > 50) {
            message = justeradeRader;
        } else if (this.authenticationService.hasRole(RaskRoller.role.Arbetsordersystemadmin)) {
            message = `Arbetsordern har ${this.arbetsorder.antalRader} rader.`;
        }

        if (ObjectUtils.stringHasValue(message)) {
            this.messageItemsAsObservable.value.push({
                message : message,
                severity : MessageSeverity.Info,
                anchor: "#begransa-rader",
                anchorText: hanteraRader
            });
        }

        this.messageItemsAsObservable.next(this.messageItemsAsObservable.value);
    }
}
